import React, { useEffect, useState, useCallback } from 'react';
import { MainLayout } from '@/components/Layout';
import { products, categories, orders as ordersApi, stock } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShoppingCart, X, Plus, Minus, AlertCircle, Wallet, User, Truck, Search, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PDVPage() {
  const navigate = useNavigate();
  const [productsList, setProductsList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Cash validation state
  const [cashStatus, setCashStatus] = useState({ isOpen: false, checked: false });
  const [showCashWarning, setShowCashWarning] = useState(false);
  
  // NOVOS ESTADOS - Cliente e Entrega
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  
  // Entrega
  const [deliveryType, setDeliveryType] = useState('PICKUP'); // PICKUP ou OWN_DELIVERY
  const [drivers, setDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryFeeSource, setDeliveryFeeSource] = useState(null);
  const [deliveryFeeOverridden, setDeliveryFeeOverridden] = useState(false);
  const [loadingDeliveryFee, setLoadingDeliveryFee] = useState(false);
  
  // Payment methods
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  
  // Product Options/Variations modal
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedProductForOptions, setSelectedProductForOptions] = useState(null);
  const [productOptionGroups, setProductOptionGroups] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    loadData();
    checkCashStatus();
    loadDrivers();
    loadPaymentMethods();
  }, []);

  const checkCashStatus = async () => {
    try {
      const response = await api.get(`${API_URL}/api/cash/session/current`);
      setCashStatus({
        isOpen: response.data.has_open_session,
        checked: true
      });
    } catch (error) {
      setCashStatus({ isOpen: false, checked: true });
    }
  };

  const loadData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        products.list(),
        categories.list()
      ]);
      setProductsList(productsRes.data || []);
      setCategoriesList(categoriesRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };
  
  const loadDrivers = async () => {
    try {
      const response = await api.get(`${API_URL}/api/drivers`);
      setDrivers(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar entregadores:', error);
    }
  };
  
  const loadPaymentMethods = async () => {
    try {
      const response = await api.get(`${API_URL}/api/payment-methods`);
      const methods = response.data || [];
      setPaymentMethods(methods);
      // Selecionar Dinheiro como padrão
      const cashMethod = methods.find(m => m.type === 'DINHEIRO' || m.name?.toLowerCase().includes('dinheiro'));
      if (cashMethod) {
        setSelectedPaymentMethod(cashMethod.id);
      } else if (methods.length > 0) {
        setSelectedPaymentMethod(methods[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar métodos de pagamento:', error);
    }
  };
  
  // Buscar clientes
  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setCustomerResults([]);
      return;
    }
    
    setSearchingCustomers(true);
    try {
      const response = await api.get(`${API_URL}/api/customers?search=${encodeURIComponent(query)}`);
      setCustomerResults(response.data?.items || response.data || []);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      setCustomerResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  }, []);
  
  // Debounce para busca de clientes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch) {
        searchCustomers(customerSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch, searchCustomers]);
  
  // Calcular taxa de entrega quando mudar para OWN_DELIVERY
  const calculateDeliveryFee = useCallback(async () => {
    if (deliveryType !== 'OWN_DELIVERY') {
      setDeliveryFee(0);
      setDeliveryFeeSource(null);
      return;
    }
    
    setLoadingDeliveryFee(true);
    try {
      const response = await api.post(`${API_URL}/api/menu-admin/delivery/calculate`, {
        order_subtotal: cartTotal
      });
      
      if (!deliveryFeeOverridden) {
        setDeliveryFee(response.data.delivery_fee || 0);
        setDeliveryFeeSource(response.data.fee_source);
      }
    } catch (error) {
      console.error('Erro ao calcular frete:', error);
      if (!deliveryFeeOverridden) {
        setDeliveryFee(0);
        setDeliveryFeeSource('DEFAULT');
      }
    } finally {
      setLoadingDeliveryFee(false);
    }
  }, [deliveryType, deliveryFeeOverridden]);
  
  useEffect(() => {
    calculateDeliveryFee();
  }, [deliveryType, calculateDeliveryFee]);
  
  // Selecionar cliente
  const selectCustomer = (customer) => {
    setSelectedCustomerId(customer.id);
    setSelectedCustomer(customer);
    setCustomerName(customer.name);
    setCustomerSearch('');
    setCustomerResults([]);
  };
  
  // Limpar cliente selecionado
  const clearCustomer = () => {
    setSelectedCustomerId(null);
    setSelectedCustomer(null);
    setCustomerName('');
  };
  
  // Mudar taxa de entrega manualmente
  const handleDeliveryFeeChange = (value) => {
    const fee = parseFloat(value) || 0;
    setDeliveryFee(fee);
    setDeliveryFeeOverridden(true);
    setDeliveryFeeSource('MANUAL');
  };

  // Check if product has options and open modal
  const handleProductClick = async (product) => {
    try {
      setLoadingOptions(true);
      const options = await stock.getProductOptionGroups(product.id);
      
      if (options && options.length > 0) {
        setSelectedProductForOptions(product);
        setProductOptionGroups(options);
        setSelectedOptions({});
        setShowOptionsModal(true);
      } else {
        addToCartDirect(product);
      }
    } catch (error) {
      addToCartDirect(product);
    } finally {
      setLoadingOptions(false);
    }
  };

  const addToCartDirect = (product, options = [], optionsPrice = 0) => {
    const cartItemId = options.length > 0 
      ? `${product.id}_${Date.now()}` 
      : product.id;
    
    setCart((prev) => {
      if (options.length === 0) {
        const existing = prev.find((item) => item.product_id === product.id && !item.options?.length);
        if (existing) {
          return prev.map((item) =>
            item.product_id === product.id && !item.options?.length
              ? { ...item, quantity: item.quantity + 1 }
              : item
          );
        }
      }
      
      return [
        ...prev,
        {
          cart_item_id: cartItemId,
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          unit_price: product.price + optionsPrice,
          base_price: product.price,
          options: options,
          options_price: optionsPrice
        },
      ];
    });
    
    const optionsText = options.length > 0 ? ` (${options.map(o => o.name).join(', ')})` : '';
    toast.success(`${product.name}${optionsText} adicionado`);
  };

  const addToCart = (product) => {
    handleProductClick(product);
  };

  const toggleOption = (groupId, option) => {
    setSelectedOptions(prev => {
      const groupOptions = prev[groupId] || [];
      const group = productOptionGroups.find(g => g.id === groupId);
      const maxQty = group?.max_quantity || 1;
      
      const isSelected = groupOptions.some(o => o.id === option.id);
      
      if (isSelected) {
        return {
          ...prev,
          [groupId]: groupOptions.filter(o => o.id !== option.id)
        };
      } else {
        if (groupOptions.length >= maxQty) {
          if (maxQty === 1) {
            return {
              ...prev,
              [groupId]: [option]
            };
          }
          toast.error(`Máximo de ${maxQty} opções neste grupo`);
          return prev;
        }
        return {
          ...prev,
          [groupId]: [...groupOptions, option]
        };
      }
    });
  };

  const confirmOptionsAndAdd = () => {
    for (const group of productOptionGroups) {
      if (group.is_required) {
        const selected = selectedOptions[group.id] || [];
        if (selected.length < group.min_quantity) {
          toast.error(`Selecione pelo menos ${group.min_quantity} opção em "${group.name}"`);
          return;
        }
      }
    }
    
    const allOptions = [];
    let totalOptionsPrice = 0;
    
    for (const [groupId, options] of Object.entries(selectedOptions)) {
      for (const opt of options) {
        allOptions.push({
          id: opt.id,
          name: opt.name,
          price: opt.price_modifier || 0,
          group_id: groupId
        });
        totalOptionsPrice += opt.price_modifier || 0;
      }
    }
    
    addToCartDirect(selectedProductForOptions, allOptions, totalOptionsPrice);
    setShowOptionsModal(false);
    setSelectedProductForOptions(null);
    setProductOptionGroups([]);
    setSelectedOptions({});
  };

  const updateQuantity = (cartItemId, delta) => {
    setCart((prev) =>
      prev
        .map((item) =>
          (item.cart_item_id || item.product_id) === cartItemId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const removeFromCart = (cartItemId) => {
    setCart((prev) => prev.filter((item) => (item.cart_item_id || item.product_id) !== cartItemId));
  };

  const handleFinishOrder = async () => {
    if (cart.length === 0) {
      toast.error('Carrinho vazio');
      return;
    }

    if (!cashStatus.isOpen) {
      setShowCashWarning(true);
      return;
    }
    
    if (!selectedPaymentMethod) {
      toast.error('Selecione um método de pagamento');
      return;
    }

    try {
      const payload = {
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        payment_method_id: selectedPaymentMethod,
        customer_name: customerName || 'Balcão',
        customer_id: selectedCustomerId || null,
        notes: '',
        delivery: deliveryType === 'OWN_DELIVERY' ? {
          delivery_type: deliveryType,
          driver_id: selectedDriverId,
          delivery_fee: deliveryFee,
          fee_source: deliveryFeeSource,
          fee_overridden: deliveryFeeOverridden
        } : null
      };
      
      const response = await api.post(`${API_URL}/api/pdv/quick-sale`, payload);
      
      if (response.data.success) {
        toast.success('Venda realizada com sucesso!');
        // Reset
        setCart([]);
        setCustomerName('');
        setSelectedCustomerId(null);
        setSelectedCustomer(null);
        setDeliveryType('PICKUP');
        setSelectedDriverId(null);
        setDeliveryFee(0);
        setDeliveryFeeOverridden(false);
        setShowCart(false);
      } else {
        toast.error(response.data.message || 'Erro ao finalizar venda');
      }
    } catch (error) {
      console.error('Erro ao finalizar:', error);
      toast.error('Erro ao criar pedido');
    }
  };

  const handleGoToCash = () => {
    setShowCashWarning(false);
    navigate('/cash');
  };

  const filteredProducts = selectedCategory === 'all'
    ? productsList
    : productsList.filter(p => p.category_id === selectedCategory);

  const cartTotal = cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const finalTotal = cartTotal + (deliveryType === 'OWN_DELIVERY' ? deliveryFee : 0);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Carregando...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 page-enter" data-testid="pdv-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              PDV - Ponto de Venda
            </h1>
            <p className="text-slate-600">Vendas rápidas e eficientes</p>
          </div>
          <Button
            onClick={() => setShowCart(true)}
            className="bg-[#FF6B6B] hover:bg-[#FF7A5A] relative"
            data-testid="open-cart-btn"
          >
            <ShoppingCart size={20} className="mr-2" />
            Carrinho
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#FF6B6B] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                {cartCount}
              </span>
            )}
          </Button>
        </div>

        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedCategory('all')}
            data-testid="category-all"
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'bg-[#FF6B6B] text-white'
                : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            Todos
          </button>
          {categoriesList.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              data-testid={`category-${cat.id}`}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                selectedCategory === cat.id
                  ? 'bg-[#FF6B6B] text-white'
                  : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {filteredProducts.map((product) => (
            <button
              key={product.id}
              onClick={() => addToCart(product)}
              data-testid={`product-${product.id}`}
              className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 hover:ring-2 ring-rose-500 transition-all active:scale-95 text-left"
            >
              <div className="h-24 bg-gradient-to-br from-rose-100 to-rose-200 rounded-lg mb-3 flex items-center justify-center">
                <span className="text-4xl">🍽️</span>
              </div>
              <h3 className="font-bold text-slate-900 mb-1 text-sm">{product.name}</h3>
              <p className="text-xl font-bold text-[#FF6B6B]" style={{ fontFamily: 'Barlow Condensed' }}>
                {formatCurrency(product.price)}
              </p>
            </button>
          ))}
        </div>

        {/* Modal do Carrinho */}
        <Dialog open={showCart} onOpenChange={setShowCart}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Carrinho de Compras</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              
              {/* Seção Cliente */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-slate-700 font-medium">
                  <User size={18} />
                  <span>Cliente</span>
                </div>
                
                {selectedCustomer ? (
                  <div className="flex items-center justify-between bg-white rounded-lg p-3 border">
                    <div>
                      <p className="font-medium text-slate-900">{selectedCustomer.name}</p>
                      <p className="text-sm text-slate-500">{selectedCustomer.phone || 'Sem telefone'}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={clearCustomer}>
                      <X size={16} />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <Input
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        placeholder="Buscar cliente cadastrado..."
                        className="pl-9"
                        data-testid="customer-search-input"
                      />
                    </div>
                    
                    {/* Resultados da busca */}
                    {customerResults.length > 0 && (
                      <div className="bg-white rounded-lg border max-h-40 overflow-y-auto">
                        {customerResults.map((customer) => (
                          <button
                            key={customer.id}
                            onClick={() => selectCustomer(customer)}
                            className="w-full text-left p-3 hover:bg-slate-50 border-b last:border-b-0"
                          >
                            <p className="font-medium text-slate-900">{customer.name}</p>
                            <p className="text-sm text-slate-500">{customer.phone}</p>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {searchingCustomers && (
                      <p className="text-sm text-slate-500 text-center">Buscando...</p>
                    )}
                    
                    <div className="text-center text-slate-400 text-sm">ou</div>
                    
                    <Input
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Nome avulso (opcional)"
                      data-testid="customer-name-input"
                    />
                  </div>
                )}
              </div>
              
              {/* Seção Tipo de Entrega */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-slate-700 font-medium">
                  <Truck size={18} />
                  <span>Tipo de Entrega</span>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant={deliveryType === 'PICKUP' ? 'default' : 'outline'}
                    onClick={() => setDeliveryType('PICKUP')}
                    className={deliveryType === 'PICKUP' ? 'bg-[#FF6B6B] hover:bg-[#FF7A5A]' : ''}
                    data-testid="delivery-type-pickup"
                  >
                    Retirada
                  </Button>
                  <Button
                    variant={deliveryType === 'OWN_DELIVERY' ? 'default' : 'outline'}
                    onClick={() => setDeliveryType('OWN_DELIVERY')}
                    className={deliveryType === 'OWN_DELIVERY' ? 'bg-[#FF6B6B] hover:bg-[#FF7A5A]' : ''}
                    data-testid="delivery-type-delivery"
                  >
                    Entrega Própria
                  </Button>
                </div>
                
                {/* Campos de entrega */}
                {deliveryType === 'OWN_DELIVERY' && (
                  <div className="space-y-3 pt-2">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Motoboy (opcional)</label>
                      <Select value={selectedDriverId || ''} onValueChange={setSelectedDriverId}>
                        <SelectTrigger data-testid="driver-select">
                          <SelectValue placeholder="Selecionar motoboy..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Nenhum (atribuir depois)</SelectItem>
                          {drivers.map((driver) => (
                            <SelectItem key={driver.id} value={driver.id}>
                              {driver.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">
                        Taxa de Entrega
                        {deliveryFeeSource && !deliveryFeeOverridden && (
                          <span className="text-xs text-slate-400 ml-2">({deliveryFeeSource})</span>
                        )}
                        {deliveryFeeOverridden && (
                          <span className="text-xs text-amber-500 ml-2">(editado)</span>
                        )}
                      </label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={deliveryFee}
                          onChange={(e) => handleDeliveryFeeChange(e.target.value)}
                          className="flex-1"
                          data-testid="delivery-fee-input"
                        />
                        {deliveryFeeOverridden && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setDeliveryFeeOverridden(false);
                              calculateDeliveryFee();
                            }}
                          >
                            Reset
                          </Button>
                        )}
                      </div>
                      {loadingDeliveryFee && (
                        <p className="text-xs text-slate-400 mt-1">Calculando...</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Método de Pagamento */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-slate-700 font-medium">
                  <Wallet size={18} />
                  <span>Pagamento</span>
                </div>
                <Select value={selectedPaymentMethod || ''} onValueChange={setSelectedPaymentMethod}>
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue placeholder="Selecionar método..." />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map((method) => (
                      <SelectItem key={method.id} value={method.id}>
                        {method.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Itens do Carrinho */}
              <div className="space-y-3">
                <p className="font-medium text-slate-700">Itens ({cartCount})</p>
                {cart.length > 0 ? (
                  cart.map((item) => (
                    <div key={item.cart_item_id || item.product_id} className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{item.product_name}</p>
                        {item.options && item.options.length > 0 && (
                          <p className="text-xs text-slate-500">{item.options.map(o => o.name).join(', ')}</p>
                        )}
                        <p className="text-sm text-slate-600">{formatCurrency(item.unit_price)} cada</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateQuantity(item.cart_item_id || item.product_id, -1)}
                            data-testid={`decrease-${item.product_id}`}
                          >
                            <Minus size={16} />
                          </Button>
                          <span className="font-semibold w-8 text-center">{item.quantity}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateQuantity(item.cart_item_id || item.product_id, 1)}
                            data-testid={`increase-${item.product_id}`}
                          >
                            <Plus size={16} />
                          </Button>
                        </div>
                        <p className="font-bold text-slate-900 w-24 text-right">
                          {formatCurrency(item.unit_price * item.quantity)}
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => removeFromCart(item.cart_item_id || item.product_id)}
                          data-testid={`remove-${item.product_id}`}
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-slate-500 py-8">Carrinho vazio</p>
                )}
              </div>

              {/* Cash Status Warning */}
              {!cashStatus.isOpen && cashStatus.checked && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Caixa Fechado</p>
                    <p className="text-sm text-amber-600">Abra o caixa antes de finalizar vendas.</p>
                  </div>
                </div>
              )}

              {/* Totais */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex items-center justify-between text-slate-600">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(cartTotal)}</span>
                </div>
                
                {deliveryType === 'OWN_DELIVERY' && (
                  <div className="flex items-center justify-between text-slate-600">
                    <span>Taxa de Entrega:</span>
                    <span>{formatCurrency(deliveryFee)}</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-xl font-bold text-slate-900">Total:</span>
                  <span className="text-3xl font-bold text-[#FF6B6B]" style={{ fontFamily: 'Barlow Condensed' }}>
                    {formatCurrency(finalTotal)}
                  </span>
                </div>
                
                <Button
                  onClick={handleFinishOrder}
                  className="w-full bg-[#FF6B6B] hover:bg-[#FF7A5A] text-lg py-6 mt-4"
                  data-testid="finish-order-btn"
                  disabled={(!cashStatus.isOpen && cashStatus.checked) || cart.length === 0}
                >
                  Finalizar Pedido
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Product Options Modal */}
        <Dialog open={showOptionsModal} onOpenChange={setShowOptionsModal}>
          <DialogContent className="bg-gradient-to-b from-white to-slate-50 max-w-md max-h-[85vh] overflow-hidden p-0 rounded-2xl border-0 shadow-2xl">
            {/* Header with product image/icon */}
            <div className="bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur">
                  <ShoppingCart className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold">{selectedProductForOptions?.name}</h2>
                  <p className="text-white/80 text-sm">Personalize seu pedido</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white/70">A partir de</p>
                  <p className="text-2xl font-bold">{formatCurrency(selectedProductForOptions?.price || 0)}</p>
                </div>
              </div>
            </div>
            
            {/* Options Content */}
            <div className="overflow-y-auto max-h-[45vh] p-4">
              {productOptionGroups.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Settings className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma opção configurada</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {productOptionGroups.map((group) => (
                    <div key={group.id} className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-bold text-slate-800 flex items-center gap-2">
                          {group.name}
                          {group.is_required && (
                            <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">
                              OBRIGATÓRIO
                            </span>
                          )}
                        </h4>
                        {group.max_quantity > 1 && (
                          <span className="text-xs text-slate-400">
                            Escolha até {group.max_quantity}
                          </span>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        {group.options?.filter(o => o.is_available).map((option) => {
                          const isSelected = (selectedOptions[group.id] || []).some(o => o.id === option.id);
                          return (
                            <div
                              key={option.id}
                              className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                                isSelected 
                                  ? 'bg-gradient-to-r from-[#FF6B6B]/10 to-[#FF8E53]/10 border-2 border-[#FF6B6B] shadow-md' 
                                  : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
                              }`}
                              onClick={() => toggleOption(group.id, option)}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                  isSelected 
                                    ? 'border-[#FF6B6B] bg-[#FF6B6B]' 
                                    : 'border-slate-300 bg-white'
                                }`}>
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                                <span className={`font-medium ${isSelected ? 'text-[#FF6B6B]' : 'text-slate-700'}`}>
                                  {option.name}
                                </span>
                              </div>
                              {option.price_modifier !== 0 && (
                                <span className={`font-bold text-sm px-2 py-1 rounded-lg ${
                                  option.price_modifier > 0 
                                    ? 'text-green-600 bg-green-50' 
                                    : 'text-red-600 bg-red-50'
                                }`}>
                                  {option.price_modifier > 0 ? '+' : ''}{formatCurrency(option.price_modifier)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer with total */}
            <div className="border-t bg-white p-4 space-y-3">
              {Object.values(selectedOptions).flat().length > 0 && (
                <div className="flex flex-wrap gap-2 pb-2">
                  {Object.values(selectedOptions).flat().map((opt, idx) => (
                    <span key={idx} className="text-xs bg-[#FF6B6B]/10 text-[#FF6B6B] px-2 py-1 rounded-full">
                      {opt.name}
                    </span>
                  ))}
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total do item</p>
                  <p className="text-2xl font-bold text-[#FF6B6B]">
                    {formatCurrency(
                      (selectedProductForOptions?.price || 0) + 
                      Object.values(selectedOptions).flat().reduce((sum, o) => sum + (o.price_modifier || 0), 0)
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowOptionsModal(false)}
                    className="px-4"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={confirmOptionsAndAdd} 
                    className="bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] hover:from-[#FF5252] hover:to-[#FF7043] text-white px-6 shadow-lg"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cash Warning Dialog */}
        <Dialog open={showCashWarning} onOpenChange={setShowCashWarning}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="w-6 h-6" />
                Caixa Fechado
              </DialogTitle>
              <DialogDescription>
                Para realizar vendas no PDV, é necessário abrir o caixa primeiro.
              </DialogDescription>
            </DialogHeader>
            <div className="py-6 flex justify-center">
              <div className="p-6 bg-amber-50 rounded-full">
                <Wallet className="w-16 h-16 text-amber-500" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowCashWarning(false)}>
                Cancelar
              </Button>
              <Button onClick={handleGoToCash} className="bg-[#FF6B6B] hover:bg-[#FF7A5A]">
                Abrir Caixa
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}

export default PDVPage;
